/**
 * API Keys Tab - Admin panel tab for managing OpenAI and Anthropic API keys.
 *
 * Allows superadmins to view, validate, and rotate API keys with
 * zero-downtime propagation to all services.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listApiKeys,
  setApiKey,
  validateApiKey,
  getApiKeyAudit,
  type ApiKeyInfo,
  type ApiKeyAuditEntry,
} from '../../services/adminApi';

interface KeyCardState {
  expanded: boolean;
  newValue: string;
  showValue: boolean;
  validating: boolean;
  saving: boolean;
  validationResult: { valid: boolean; error: string | null } | null;
}

const KEY_CONFIGS = [
  { key_name: 'openai_api_key', label: 'openai', provider: 'openai' },
  { key_name: 'anthropic_api_key', label: 'anthropic', provider: 'anthropic' },
  { key_name: 'groq_api_key', label: 'groq', provider: 'groq' },
] as const;

function timeAgo(dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return t('apiKeys.justNow');
  if (diffMins < 60) return t('apiKeys.minutesAgo', { count: diffMins });
  if (diffHours < 24) return t('apiKeys.hoursAgo', { count: diffHours });
  return t('apiKeys.daysAgo', { count: diffDays });
}

export default function APIKeysTab() {
  const { t } = useTranslation('admin');
  const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [cardStates, setCardStates] = useState<Record<string, KeyCardState>>({});
  const [auditEntries, setAuditEntries] = useState<ApiKeyAuditEntry[]>([]);
  const [showAudit, setShowAudit] = useState(false);
  const [skipValidation, setSkipValidation] = useState<Record<string, boolean>>({});

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const result = await listApiKeys();
      setKeys(result.keys);
    } catch {
      // Keys may not exist yet, that's fine
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    try {
      const result = await getApiKeyAudit(undefined, 50);
      setAuditEntries(result.entries);
    } catch {
      setAuditEntries([]);
    }
  }, []);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const getCardState = (keyName: string): KeyCardState => {
    return cardStates[keyName] || {
      expanded: false,
      newValue: '',
      showValue: false,
      validating: false,
      saving: false,
      validationResult: null,
    };
  };

  const updateCardState = (keyName: string, updates: Partial<KeyCardState>) => {
    setCardStates(prev => ({
      ...prev,
      [keyName]: { ...getCardState(keyName), ...updates },
    }));
  };

  const getKeyInfo = (keyName: string): ApiKeyInfo | undefined => {
    return keys.find(k => k.key_name === keyName);
  };

  const handleValidate = async (keyName: string, provider: string) => {
    const state = getCardState(keyName);
    if (!state.newValue.trim()) return;

    updateCardState(keyName, { validating: true, validationResult: null });
    try {
      const result = await validateApiKey({ provider, value: state.newValue });
      updateCardState(keyName, { validating: false, validationResult: { valid: result.valid, error: result.error } });
    } catch {
      updateCardState(keyName, { validating: false, validationResult: { valid: false, error: 'Request failed' } });
    }
  };

  const handleSave = async (keyName: string) => {
    const state = getCardState(keyName);
    if (!state.newValue.trim()) return;

    updateCardState(keyName, { saving: true });
    try {
      await setApiKey({
        key_name: keyName,
        value: state.newValue,
        skip_validation: skipValidation[keyName] || false,
      });
      updateCardState(keyName, {
        saving: false,
        expanded: false,
        newValue: '',
        validationResult: null,
        showValue: false,
      });
      await loadKeys();
    } catch {
      updateCardState(keyName, { saving: false });
    }
  };

  const canSave = (keyName: string): boolean => {
    const state = getCardState(keyName);
    if (!state.newValue.trim()) return false;
    if (skipValidation[keyName]) return true;
    return state.validationResult?.valid === true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">{t('apiKeys.title')}</h2>
        <button
          onClick={loadKeys}
          className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {t('apiKeys.refresh')}
        </button>
      </div>

      {/* Key Cards */}
      <div className="space-y-4">
        {KEY_CONFIGS.map(({ key_name, label, provider }) => {
          const keyInfo = getKeyInfo(key_name);
          const state = getCardState(key_name);

          return (
            <div key={key_name} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {/* Key Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-medium text-gray-900">{t(`apiKeys.${label}`)}</h3>
                  <div className="flex items-center gap-3 mt-1">
                    {keyInfo ? (
                      <>
                        <span className="text-sm text-gray-500">
                          {t('apiKeys.currentKey')}: <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{keyInfo.key_hint}</code>
                        </span>
                        {keyInfo.is_valid !== null && (
                          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                            keyInfo.is_valid
                              ? 'bg-green-50 text-green-700'
                              : 'bg-red-50 text-red-700'
                          }`}>
                            {keyInfo.is_valid ? (
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            )}
                            {keyInfo.is_valid ? t('apiKeys.valid') : t('apiKeys.invalid')}
                          </span>
                        )}
                        {keyInfo.updated_at && (
                          <span className="text-xs text-gray-400">
                            {t('apiKeys.updated')} {timeAgo(keyInfo.updated_at, t)}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-sm text-gray-400 italic">{t('apiKeys.notSet')}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => updateCardState(key_name, { expanded: !state.expanded, validationResult: null, newValue: '' })}
                  className="px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
                >
                  {state.expanded ? t('apiKeys.cancel') : t('apiKeys.changeKey')}
                </button>
              </div>

              {/* Expanded: Change Key Form */}
              {state.expanded && (
                <div className="mt-4 pt-4 border-t border-gray-100 space-y-3">
                  {/* Key Input */}
                  <div className="relative">
                    <input
                      type={state.showValue ? 'text' : 'password'}
                      value={state.newValue}
                      onChange={(e) => updateCardState(key_name, { newValue: e.target.value, validationResult: null })}
                      placeholder={t('apiKeys.newKeyPlaceholder')}
                      className="w-full px-3 py-2 pr-16 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={() => updateCardState(key_name, { showValue: !state.showValue })}
                      className="absolute right-2 top-1/2 -translate-y-1/2 px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                    >
                      {state.showValue ? t('apiKeys.hidePassword') : t('apiKeys.showPassword')}
                    </button>
                  </div>

                  {/* Validation Result */}
                  {state.validationResult && (
                    <div className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg ${
                      state.validationResult.valid
                        ? 'bg-green-50 text-green-700'
                        : 'bg-red-50 text-red-700'
                    }`}>
                      {state.validationResult.valid ? (
                        <>
                          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {t('apiKeys.validationSuccess')}
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          {t('apiKeys.validationFailed')}: {state.validationResult.error}
                        </>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleValidate(key_name, provider)}
                      disabled={!state.newValue.trim() || state.validating}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {state.validating ? t('apiKeys.validating') : t('apiKeys.validate')}
                    </button>
                    <button
                      onClick={() => handleSave(key_name)}
                      disabled={!canSave(key_name) || state.saving}
                      className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {state.saving ? t('apiKeys.saving') : t('apiKeys.save')}
                    </button>
                    <label className="flex items-center gap-2 text-xs text-gray-500 ml-auto">
                      <input
                        type="checkbox"
                        checked={skipValidation[key_name] || false}
                        onChange={(e) => setSkipValidation(prev => ({ ...prev, [key_name]: e.target.checked }))}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      {t('apiKeys.skipValidation')}
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Audit History (collapsible) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <button
          onClick={() => {
            const next = !showAudit;
            setShowAudit(next);
            if (next) loadAudit();
          }}
          className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors rounded-xl"
        >
          <span className="text-sm font-medium text-gray-700">{t('apiKeys.changeHistory')}</span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${showAudit ? 'rotate-180' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showAudit && (
          <div className="px-6 pb-4 border-t border-gray-100">
            {auditEntries.length === 0 ? (
              <p className="text-sm text-gray-400 py-4">{t('apiKeys.noHistory')}</p>
            ) : (
              <table className="w-full text-sm mt-3">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase">
                    <th className="pb-2 pr-4">{t('apiKeys.action')}</th>
                    <th className="pb-2 pr-4">{t('apiKeys.keyHint')}</th>
                    <th className="pb-2 pr-4">{t('apiKeys.performedBy')}</th>
                    <th className="pb-2">{t('apiKeys.performedAt')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {auditEntries.map((entry) => (
                    <tr key={entry.id} className="text-gray-700">
                      <td className="py-2 pr-4">
                        <span className="inline-flex items-center gap-1">
                          <span className="font-medium">{entry.key_name.replace('_api_key', '')}</span>
                          <span className="text-gray-400">-</span>
                          <span>{t(`apiKeys.${entry.action}`)}</span>
                        </span>
                      </td>
                      <td className="py-2 pr-4">
                        <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs">{entry.key_hint}</code>
                      </td>
                      <td className="py-2 pr-4 text-gray-500">
                        {entry.performed_by ? `User #${entry.performed_by}` : '-'}
                      </td>
                      <td className="py-2 text-gray-500">
                        {entry.performed_at ? timeAgo(entry.performed_at, t) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
